import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Footer } from '../components/Footer';
import { MapPin, Phone, Clock, ChevronRight, Image as ImageIcon } from 'lucide-react';

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'branches'));
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBranches(data);
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-20" data-testid="branches-page">
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl font-montserrat font-bold text-white mb-4">
              Filiallarımız
            </h1>
            {branches.length > 0 && (
              <p className="text-xl text-gray-400 font-medium">
                Bakı şəhərində {branches.length} müxtəlif yerdə xidmət göstəririk
              </p>
            )}
          </motion.div>

          {branches.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {branches.map((branch, index) => (
                <motion.div
                  key={branch.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-dark rounded-xl overflow-hidden card-hover border border-gray-700 group"
                  data-testid={`branch-${branch.id}`}
                >
                {/* Branch Image - use coverImage first, then first gallery image */}
                  {branch.coverImage ? (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={branch.coverImage} 
                        alt={branch.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : branch.images && branch.images.length > 0 ? (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={branch.images[0]} 
                        alt={branch.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#D90429]/20 to-[#0A0A0A] flex items-center justify-center">
                      <MapPin className="w-16 h-16 text-[#D90429]/50" />
                    </div>
                  )}

                  <div className="p-6">
                    <h3 className="text-2xl font-bold mb-3 text-white">{branch.name}</h3>
                    <p className="text-gray-400 mb-4 font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-red-500" />
                      {branch.address}
                    </p>
                    
                    {/* Phone Numbers */}
                    {branch.phones && branch.phones.length > 0 ? (
                      <div className="space-y-1 mb-3">
                        {branch.phones.slice(0, 2).map((phone, pIndex) => (
                          <div key={pIndex} className="flex items-center gap-2 text-gray-400">
                            <Phone className="w-4 h-4 text-red-500" />
                            <span className="text-gray-500 text-sm">{phone.label}:</span>
                            <span className="font-medium">{phone.number}</span>
                          </div>
                        ))}
                        {branch.phones.length > 2 && (
                          <span className="text-gray-500 text-sm ml-6">+{branch.phones.length - 2} daha çox</span>
                        )}
                      </div>
                    ) : branch.phone && (
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Phone className="w-4 h-4 text-red-500" />
                        <span className="font-medium">{branch.phone}</span>
                      </div>
                    )}
                    
                    {branch.workingHours && (
                      <div className="flex items-center gap-2 text-gray-400 mb-4">
                        <Clock className="w-4 h-4 text-red-500" />
                        <span className="font-medium">{branch.workingHours}</span>
                      </div>
                    )}

                    {branch.images && branch.images.length > 0 && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                        <ImageIcon className="w-4 h-4" />
                        <span>{branch.images.length} şəkil</span>
                      </div>
                    )}
                    
                    <div className="flex gap-3">
                      <Link 
                        to={`/branches/${branch.id}`}
                        className="flex-1 btn-primary text-white px-4 py-3 rounded-lg font-bold text-center flex items-center justify-center gap-2 group-hover:bg-[#B00220] transition-colors"
                      >
                        Ətraflı bax
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                      {branch.mapUrl && (
                        <a 
                          href={branch.mapUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-4 py-3 border border-[#D90429] text-[#D90429] rounded-lg font-bold hover:bg-[#D90429]/10 transition-colors"
                        >
                          <MapPin className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <MapPin className="w-20 h-20 text-gray-600 mx-auto mb-6" />
              <h3 className="text-2xl text-white mb-3">Hələ filial əlavə edilməyib</h3>
              <p className="text-gray-400">Admin paneldən filial əlavə edin</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
